/**
 * Admin Dashboard Page
 * System administration dashboard for managing manufacturers and viewing system stats
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, SystemStats, ManufacturerDetails } from '../services/admin.service';
import { Button, MetricCard, PageHeader, StatusBadge } from '../components/ui';
import { EnhancedStatisticsDashboard } from '../components/EnhancedStatisticsDashboard';
import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import { ManufacturerSelector } from '../components/ManufacturerSelector';
import { ManufacturerRegistrationReview } from '../components/ManufacturerRegistrationReview';
import AnomalyAnalyticsDashboard from '../components/AnomalyAnalyticsDashboard';
import { mapStatusToBadge } from '../lib/status';

const AdminDashboardContent: React.FC = () => {
  const navigate = useNavigate();
  const { selectedManufacturer } = useNavigation();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [manufacturers, setManufacturers] = useState<ManufacturerDetails[]>([]);
  const [pendingManufacturers, setPendingManufacturers] = useState<ManufacturerDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'manufacturers' | 'pending' | 'gas-analytics' | 'anomaly-detection'>('overview');
  const [showRegistrationReview, setShowRegistrationReview] = useState(false);
  const [selectedManufacturerForReview, setSelectedManufacturerForReview] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    
    // Set up auto-refresh every 30 seconds for pending manufacturers
    const interval = setInterval(() => {
      if (activeTab === 'pending' || activeTab === 'overview') {
        loadData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 AdminDashboard: Loading data...');

      const [statsData, manufacturersData, pendingData] = await Promise.all([
        adminService.getSystemStats(),
        adminService.getAllManufacturers(),
        adminService.getPendingManufacturers(),
      ]);

      console.log('📊 Stats:', statsData);
      console.log('🏭 All Manufacturers:', manufacturersData);
      console.log('⏳ Pending Manufacturers:', pendingData);

      setStats(statsData);
      setManufacturers(manufacturersData);
      setPendingManufacturers(pendingData);
    } catch (err: any) {
      console.error('❌ Error loading admin data:', err);
      setError(err.response?.data?.error || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRegistration = (id: string) => {
    setSelectedManufacturerForReview(id);
    setShowRegistrationReview(true);
  };

  const handleApprove = async (id: string) => {
    if (!confirm('Are you sure you want to approve this manufacturer?')) return;

    try {
      await adminService.approveManufacturer(id);
      setShowRegistrationReview(false);
      await loadData();
    } catch (err: any) {
      alert('Failed to approve manufacturer: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (id: string) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled

    try {
      await adminService.rejectManufacturer(id, reason);
      setShowRegistrationReview(false);
      await loadData();
    } catch (err: any) {
      alert('Failed to reject manufacturer: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <PageHeader
            title="Admin Dashboard"
            description="System administration and management"
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => navigate('/admin/anomaly/policy')}>
                  Anomaly Policy
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/anomaly/flags')}>
                  Anomaly Flags
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/operation-costs')}>
                  Operation Costs
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/credit-management')}>
                  Credit Management
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/trial-management')}>
                  Trial Management
                </Button>
                <Button variant="outline" onClick={() => navigate('/admin/issue-reports')}>
                  Issue Inbox
                </Button>
              </div>
            }
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <div className="flex justify-between items-center">
              <nav className="flex -mb-px" role="tablist" aria-label="Admin dashboard sections">
              <button
                type="button"
                onClick={() => setActiveTab('overview')}
                role="tab"
                aria-selected={activeTab === 'overview'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('manufacturers')}
                role="tab"
                aria-selected={activeTab === 'manufacturers'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'manufacturers'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Manufacturers ({manufacturers.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('pending')}
                role="tab"
                aria-selected={activeTab === 'pending'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'pending'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approvals ({pendingManufacturers.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('gas-analytics')}
                role="tab"
                aria-selected={activeTab === 'gas-analytics'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'gas-analytics'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Gas Analytics
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('anomaly-detection')}
                role="tab"
                aria-selected={activeTab === 'anomaly-detection'}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'anomaly-detection'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Anomaly Detection
              </button>
              </nav>
              
              {/* Refresh Button */}
              <div className="px-6 py-3">
                <button
                  onClick={loadData}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard title="Total Manufacturers" value={stats.totalManufacturers.toLocaleString()} icon="🏭" tone="blue" />
              <MetricCard title="Total Users" value={stats.totalUsers.toLocaleString()} icon="👥" tone="green" />
              <MetricCard title="Total Products" value={stats.totalProducts.toLocaleString()} icon="📦" tone="purple" />
              <MetricCard title="Total Scans" value={stats.totalScans.toLocaleString()} icon="📊" tone="indigo" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MetricCard title="Pending Manufacturers" value={stats.pendingManufacturers.toLocaleString()} icon="⏳" tone="yellow" />
              <MetricCard title="Pending Users" value={stats.pendingUsers.toLocaleString()} icon="⏳" tone="orange" />
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {stats.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All Manufacturers Tab */}
        {activeTab === 'manufacturers' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" aria-label="All manufacturers">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manufacturer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {manufacturers.map((manufacturer) => {
                  const statusBadge = mapStatusToBadge(manufacturer.status);
                  return (
                    <tr key={manufacturer.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{manufacturer.name}</div>
                          <div className="text-sm text-gray-500">{manufacturer.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge tone={statusBadge.tone} size="sm">
                          {statusBadge.label}
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {manufacturer.stats.totalUsers}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {manufacturer.stats.totalProducts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(manufacturer.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pending Manufacturers Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingManufacturers.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500">No pending manufacturer approvals</p>
              </div>
            ) : (
              pendingManufacturers.map((manufacturer) => (
                <div key={manufacturer.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{manufacturer.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{manufacturer.email}</p>
                      <div className="mt-4 grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Users</p>
                          <p className="text-lg font-semibold text-gray-900">{manufacturer.stats.totalUsers}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Products</p>
                          <p className="text-lg font-semibold text-gray-900">{manufacturer.stats.totalProducts}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Batches</p>
                          <p className="text-lg font-semibold text-gray-900">{manufacturer.stats.totalBatches}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Registered: {new Date(manufacturer.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        onClick={() => handleViewRegistration(manufacturer.id)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        View Details
                      </Button>
                      <Button onClick={() => handleApprove(manufacturer.id)}>
                        Approve
                      </Button>
                      <Button variant="outline" onClick={() => handleReject(manufacturer.id)}>
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Anomaly Detection Tab */}
        {activeTab === 'anomaly-detection' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Anomaly Detection Management</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Monitor and manage anomaly detection system
                </p>
              </div>
              
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <button
                  onClick={() => navigate('/admin/anomaly/policy')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-gray-900">Policy Configuration</h4>
                      <p className="text-sm text-gray-600">Configure detection parameters</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/admin/anomaly/flags')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-gray-900">Manage Flags</h4>
                      <p className="text-sm text-gray-600">Review and resolve anomaly flags</p>
                    </div>
                  </div>
                </button>

                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-gray-900">System Status</h4>
                      <p className="text-sm text-gray-600">Active and monitoring</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Manufacturer Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Manufacturer (optional)
                </label>
                <ManufacturerSelector
                  manufacturers={manufacturers.map(m => ({
                    ...m,
                    status: m.status as 'approved' | 'pending' | 'rejected'
                  }))}
                  showAllOption={true}
                  className="max-w-md"
                />
              </div>

              {/* Analytics Display */}
              <div className="mt-6">
                {selectedManufacturer ? (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm text-blue-800">
                        Showing anomaly analytics for: <strong>{manufacturers.find(m => m.id === selectedManufacturer)?.name || 'Selected Manufacturer'}</strong>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        Showing <strong>system-wide anomaly analytics</strong> across all manufacturers
                      </p>
                    </div>
                  </div>
                )}
                
                <AnomalyAnalyticsDashboard manufacturerId={selectedManufacturer || undefined} />
              </div>
            </div>
          </div>
        )}

        {/* Gas Analytics Tab */}
        {activeTab === 'gas-analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Analytics Dashboard</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select a manufacturer to view their specific analytics, or view system-wide data
                </p>
              </div>
              
              {/* Manufacturer Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manufacturer Selection
                </label>
                <ManufacturerSelector
                  manufacturers={manufacturers.map(m => ({
                    ...m,
                    status: m.status as 'approved' | 'pending' | 'rejected'
                  }))}
                  showAllOption={true}
                  className="max-w-md"
                />
              </div>

              {/* Analytics Display */}
              <div className="mt-6">
                {selectedManufacturer ? (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm text-blue-800">
                        Showing analytics for: <strong>{manufacturers.find(m => m.id === selectedManufacturer)?.name || 'Selected Manufacturer'}</strong>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm text-gray-700">
                        Showing <strong>system-wide analytics</strong> across all manufacturers
                      </p>
                    </div>
                  </div>
                )}
                
                <EnhancedStatisticsDashboard manufacturerId={selectedManufacturer || undefined} />
              </div>
            </div>
          </div>
        )}
      {/* Registration Review Modal */}
      {selectedManufacturerForReview && (
        <ManufacturerRegistrationReview
          manufacturerId={selectedManufacturerForReview}
          isOpen={showRegistrationReview}
          onClose={() => {
            setShowRegistrationReview(false);
            setSelectedManufacturerForReview(null);
          }}
          onApprove={() => handleApprove(selectedManufacturerForReview)}
          onReject={() => handleReject(selectedManufacturerForReview)}
        />
      )}
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  return (
    <NavigationProvider>
      <AdminDashboardContent />
    </NavigationProvider>
  );
};

export default AdminDashboard;

